import { useState, useEffect, useCallback } from 'react';
import { DataTable, Button, Modal } from '@sundkortid/ui';
import type { PaginationMeta, UserRole } from '@sundkortid/shared';
import { api } from '../../api/client';
import { useAuthStore } from '../../stores/auth';
import { AdminNav } from './AdminDashboardPage';

interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  phone: string | null;
  kennitala: string | null;
  createdAt: string;
}

interface Pool {
  id: string;
  name: string;
}

interface PoolAssignment {
  id: string;
  poolId: string;
  poolName: string;
  isActive: boolean;
  assignedAt: string;
}

const ROLE_LABELS: Record<UserRole, string> = {
  user: 'Notandi',
  staff: 'Starfsmaður',
  admin: 'Stjórnandi',
  super_admin: 'Yfirstjórnandi',
};

export function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [roleModal, setRoleModal] = useState<AdminUser | null>(null);
  const [newRole, setNewRole] = useState<UserRole>('user');
  const [suspendModal, setSuspendModal] = useState<AdminUser | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [poolModal, setPoolModal] = useState<AdminUser | null>(null);
  const [poolAssignments, setPoolAssignments] = useState<PoolAssignment[]>([]);
  const [allPools, setAllPools] = useState<Pool[]>([]);
  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [poolLoading, setPoolLoading] = useState(false);

  const currentUser = useAuthStore((s) => s.user);
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), perPage: '20' });
      if (search) params.set('search', search);
      if (roleFilter) params.set('role', roleFilter);

      const result = await api.getWithMeta<AdminUser[]>(`/admin/users?${params}`);
      setUsers(result.data);
      setMeta(result.meta ?? null);
    } catch {
      // handled by error boundary
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => { setPage(1); }, [search, roleFilter]);

  const handleChangeRole = async () => {
    if (!roleModal) return;
    setActionLoading(true);
    try {
      await api.patch(`/admin/users/${roleModal.id}/role`, { role: newRole });
      setRoleModal(null);
      fetchUsers();
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!suspendModal) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${suspendModal.id}/suspend`);
      setSuspendModal(null);
      fetchUsers();
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const openPoolModal = async (user: AdminUser) => {
    setPoolModal(user);
    setPoolLoading(true);
    try {
      const [assignments, pools] = await Promise.all([
        api.get<PoolAssignment[]>(`/admin/users/${user.id}/pool-assignments`),
        api.get<Pool[]>('/pools'),
      ]);
      setPoolAssignments(assignments);
      setAllPools(pools);
    } catch {
    } finally {
      setPoolLoading(false);
    }
  };

  const handleAssignPool = async () => {
    if (!poolModal || !selectedPoolId) return;
    setPoolLoading(true);
    try {
      await api.post('/admin/pool-staff', { userId: poolModal.id, poolId: selectedPoolId });
      const assignments = await api.get<PoolAssignment[]>(`/admin/users/${poolModal.id}/pool-assignments`);
      setPoolAssignments(assignments);
      setSelectedPoolId('');
    } catch {
    } finally {
      setPoolLoading(false);
    }
  };

  const handleRemovePool = async (assignmentId: string) => {
    if (!poolModal) return;
    setPoolLoading(true);
    try {
      await api.delete(`/admin/pool-staff/${assignmentId}`);
      const assignments = await api.get<PoolAssignment[]>(`/admin/users/${poolModal.id}/pool-assignments`);
      setPoolAssignments(assignments);
    } catch {
    } finally {
      setPoolLoading(false);
    }
  };

  const columns = [
    { key: 'fullName', header: 'Nafn' },
    { key: 'email', header: 'Netfang' },
    {
      key: 'role',
      header: 'Hlutverk',
      render: (row: AdminUser) => (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
          row.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
          row.role === 'admin' ? 'bg-blue-100 text-blue-800' :
          row.role === 'staff' ? 'bg-green-100 text-green-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {ROLE_LABELS[row.role]}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Skráning',
      render: (row: AdminUser) => new Date(row.createdAt).toLocaleDateString('is-IS'),
    },
    {
      key: 'actions',
      header: 'Aðgerðir',
      render: (row: AdminUser) => (
        <div className="flex gap-2">
          {(row.role === 'staff' || row.role === 'admin') && (
            <Button size="sm" variant="outline" onClick={() => openPoolModal(row)}>
              Sundlaug
            </Button>
          )}
          {isSuperAdmin && (
            <Button size="sm" variant="outline" onClick={() => { setRoleModal(row); setNewRole(row.role); }}>
              Hlutverk
            </Button>
          )}
          <Button size="sm" variant="danger" onClick={() => setSuspendModal(row)}>
            Loka
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-6xl mx-auto flex flex-col gap-4">
          <h1 className="text-xl font-title font-bold text-primary">Sundkortið — Stjórnborð</h1>
          <AdminNav />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-2xl font-title font-bold text-primary mb-6">Notendur</h2>

        <div className="flex flex-wrap gap-3 mb-4">
          <input
            type="text"
            placeholder="Leita að nafni eða netfangi..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[200px]"
          />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Öll hlutverk</option>
            <option value="user">Notandi</option>
            <option value="staff">Starfsmaður</option>
            <option value="admin">Stjórnandi</option>
            <option value="super_admin">Yfirstjórnandi</option>
          </select>
        </div>

        {loading ? (
          <p className="text-gray-500">Hleð gögnum...</p>
        ) : (
          <>
            <DataTable columns={columns} data={users} keyExtractor={(row) => row.id} />
            {meta && meta.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Fyrri
                </Button>
                <span className="text-sm text-gray-600">Síða {meta.page} af {meta.totalPages}</span>
                <Button size="sm" variant="outline" disabled={page >= meta.totalPages} onClick={() => setPage(page + 1)}>
                  Næsta
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      <Modal open={!!roleModal} onClose={() => setRoleModal(null)} title="Breyta hlutverki">
        <p className="text-sm text-gray-600 mb-4">{roleModal?.fullName} ({roleModal?.email})</p>
        <select
          value={newRole}
          onChange={(e) => setNewRole(e.target.value as UserRole)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full mb-4"
        >
          <option value="user">Notandi</option>
          <option value="staff">Starfsmaður</option>
          <option value="admin">Stjórnandi</option>
          <option value="super_admin">Yfirstjórnandi</option>
        </select>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setRoleModal(null)}>Hætta við</Button>
          <Button loading={actionLoading} onClick={handleChangeRole}>Vista</Button>
        </div>
      </Modal>

      <Modal open={!!suspendModal} onClose={() => setSuspendModal(null)} title="Loka aðgangi">
        <p className="text-sm text-gray-600 mb-4">
          Ertu viss um að þú viljir loka aðgangi <strong>{suspendModal?.fullName}</strong>?
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setSuspendModal(null)}>Hætta við</Button>
          <Button variant="danger" loading={actionLoading} onClick={handleSuspend}>Loka aðgangi</Button>
        </div>
      </Modal>

      <Modal open={!!poolModal} onClose={() => { setPoolModal(null); setSelectedPoolId(''); }} title="Úthluta sundlaug">
        <p className="text-sm text-gray-600 mb-4">{poolModal?.fullName} ({poolModal?.email})</p>

        {poolLoading && !poolAssignments.length ? (
          <p className="text-sm text-gray-500">Hleð...</p>
        ) : (
          <>
            {poolAssignments.length > 0 ? (
              <div className="space-y-2 mb-4">
                <p className="text-xs font-medium text-gray-500 uppercase">Úthlutaðar sundlaugar</p>
                {poolAssignments.map((a) => (
                  <div key={a.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-900">{a.poolName}</span>
                    <Button size="sm" variant="danger" onClick={() => handleRemovePool(a.id)}>
                      Fjarlægja
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-4">Engin sundlaug úthlutað.</p>
            )}

            {(() => {
              const assignedPoolIds = new Set(poolAssignments.map((a) => a.poolId));
              const availablePools = allPools.filter((p) => !assignedPoolIds.has(p.id));
              if (availablePools.length === 0) return null;
              return (
                <div className="flex gap-2">
                  <select
                    value={selectedPoolId}
                    onChange={(e) => setSelectedPoolId(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1"
                  >
                    <option value="">Veldu sundlaug…</option>
                    {availablePools.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <Button disabled={!selectedPoolId} loading={poolLoading} onClick={handleAssignPool}>
                    Bæta við
                  </Button>
                </div>
              );
            })()}
          </>
        )}
      </Modal>
    </div>
  );
}
